import {
  CalendarIcon,
  CalendarUnsetIcon,
  CarIcon,
} from "@/app/_components/general/Icons";
import { CalDelivery } from "@/app/types/types";
import dayjs, { Dayjs } from "dayjs";
import { Dispatch, SetStateAction, useContext } from "react";
import utc from "dayjs/plugin/utc";
import { User, UserContext } from "@/app/_components/context/user-context";

dayjs.extend(utc);

interface DeliveryCardProps {
  delivery: CalDelivery;
  setSelectedDelivery: Dispatch<SetStateAction<CalDelivery>>;
  setShowDeliveryPopUp: Dispatch<SetStateAction<boolean>>;
  refresh: () => Promise<void>;
  canEdit: boolean;
  canSeeOthersCustomers: boolean;
}

const DeliveryCalendarCardDesktop = ({
  delivery,
  setSelectedDelivery,
  setShowDeliveryPopUp,
  canEdit,
  canSeeOthersCustomers,
}: DeliveryCardProps) => {
  const { user } = useContext(UserContext);
  const handleClick = () => {
    setSelectedDelivery(delivery);
    setShowDeliveryPopUp(true);
  };

  const isOverdue = (date: Dayjs) => {
    const now = dayjs().utc().local();
    const oneDayAfter = dayjs(date).utc().local().endOf("day");
    return now.isAfter(oneDayAfter);
  };

  return (
    <button
      disabled={!canEdit}
      className={`w-[95%] flex flex-col justify-start items-center text-[0.5vw] outline outline-1 outline-gray-200 min-h-[140px] h-[140px] rounded-sm  ${canEdit && "hover:scale-105 hover:shadow-lg"}`}
      onClick={handleClick}
    >
      <div
        className="flex flex-row w-full justify-between px-3 pt-3 pb-2 rounded-t-sm"
        style={{
          backgroundColor: delivery.delivered
            ? "#d6ffff"
            : isOverdue(dayjs(delivery.scheduledDeliveryDate))
              ? "#ffd1d1"
              : "#dcf8e2",
        }}
      >
        <div className="flex font-semibold text-ellipsis overflow-hidden text-nowrap items-center justify-start text-[0.5vw] w-[30%] mr-1">
          {delivery.stockNumber}
        </div>
        <div className="flex flex-col text-ellipsis  items-center justify-center text-nowrap text-[0.40vw] text-gray-700 w-[33%]">
          <p>{delivery.salesPerson}</p>
          <p>{delivery.fsmManagerName ? delivery.fsmManagerName : "*****"}</p>
        </div>
        <div className="flex w-[33%] text-ellipsis overflow-hidden text-[0.5vw] items-center justify-end font-semibold">
          {dayjs(delivery.scheduledDeliveryDate).format("h:mm A")}
        </div>
      </div>

      <div className="flex flex-row justify-between w-full px-3 py-3">
        <div className="flex flex-col items-start justify-between text-left">
          <div>
            {delivery.salesPerson === user?.name || canSeeOthersCustomers
              ? delivery.customer
              : "********"}
          </div>
          <div>{delivery.carDescription}</div>
          <div className="flex text-ellipsis overflow-hidden text-[0.45vw] text-gray-700">
            {delivery.vin ? delivery.vin : ""}
          </div>
        </div>
        <div className="flex flex-col items-end">
          <div className="p-4">
            {delivery.delivered
              ? CarIcon
              : delivery.status === "SCHEDULED" &&
                  !delivery.delivered &&
                  isOverdue(dayjs(delivery.scheduledDeliveryDate))
                ? CalendarUnsetIcon
                : CalendarIcon}
          </div>
          <div>
            {delivery.delivered
              ? "Delivered"
              : isOverdue(dayjs(delivery.scheduledDeliveryDate))
                ? "Past Delivery"
                : "Scheduled"}
          </div>
        </div>
      </div>
    </button>
  );
};

interface DeliveryCardPropsStatic {
  delivery: CalDelivery;
  userPermissions: string[];
  user: User;
}

export const DeliveryCalendarCardDesktopStatic = ({
  delivery,
  userPermissions,
  user,
}: DeliveryCardPropsStatic) => {
  const isOverdue = (date: Dayjs) => {
    const now = dayjs().utc().local();
    const oneDayAfter = dayjs(date).utc().local().endOf("day");
    return now.isAfter(oneDayAfter);
  };

  return (
    <div className="w-[95%] flex flex-col justify-start items-center text-[0.5vw]  outline outline-1 outline-gray-200 rounded-sm h-[140px]  ">
      <div
        className="flex flex-row w-full justify-between px-3 pt-3 pb-2 rounded-t-sm"
        style={{
          backgroundColor: delivery.delivered
            ? "#d6ffff"
            : isOverdue(dayjs(delivery.scheduledDeliveryDate))
              ? "#ffd1d1"
              : "#dcf8e2",
        }}
      >
        <div className="flex font-semibold text-ellipsis overflow-hidden text-nowrap items-center justify-start text-[0.5vw] w-[30%] mr-1">
          {delivery.stockNumber}
        </div>
        <div className="flex flex-col text-ellipsis overflow-hidden items-center justify-center text-nowrap text-[0.40vw] text-gray-700 w-[33%]">
          <p>{delivery.salesPerson}</p>
          <p>{delivery.fsmManagerName ? delivery.fsmManagerName : "*****"}</p>
        </div>
        <div className="flex w-[33%] text-ellipsis overflow-hidden text-[0.5vw] items-center justify-end font-semibold">
          {dayjs(delivery.scheduledDeliveryDate).format("h:mm A")}
        </div>
      </div>

      <div className="flex flex-row justify-between w-full px-3 py-3">
        <div className="flex flex-col items-start justify-between text-left">
          <div>
            {delivery.salesPerson === user?.name ||
            userPermissions.includes("VIEW_OTHER_CUSTOMERS")
              ? delivery.customer
              : "********"}
          </div>
          <div>{delivery.carDescription}</div>
          <div className="flex text-ellipsis overflow-hidden text-[0.45vw] text-gray-700">
            {delivery.vin ? delivery.vin : ""}
          </div>
        </div>
        <div className="flex flex-col items-end">
          <div className="p-4">
            {delivery.delivered
              ? CarIcon
              : delivery.status === "SCHEDULED" &&
                  !delivery.delivered &&
                  isOverdue(dayjs(delivery.scheduledDeliveryDate))
                ? CalendarUnsetIcon
                : CalendarIcon}
          </div>
          <div>
            {delivery.delivered
              ? "Delivered"
              : isOverdue(dayjs(delivery.scheduledDeliveryDate))
                ? "Past Delivery "
                : "Scheduled"}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeliveryCalendarCardDesktop;
